import { useEffect, useRef, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sparkles, Send, RefreshCcw, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const SESSION_ID = "default";
const SUGGESTIONS = [
  "Which NPs code the highest per visit? Show avg revenue per visit.",
  "How many visits per week is each NP doing on average?",
  "Which facilities have not been visited in the last 30 days?",
  "Which NP has the most 99214/99215 codes? Are any coding suspiciously low?",
  "Total CPT revenue last 30 days by NP — rank them.",
  "Which buildings are getting fewer visits than their monthly target?",
];

export default function AiChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pending, setPending] = useState("");
  const scrollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/ai/history/${SESSION_ID}`);
      setMessages(data.map((m, i) => ({ ...m, _mid: m._mid || `${m.role}-${i}-${m.created_at || ""}` })));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[ai] history load failed:", e?.message || e);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pending]);

  const send = async (msg) => {
    const text = (msg ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    const userMid = `u-${Date.now()}`;
    setMessages((prev) => [...prev, { _mid: userMid, role: "user", content: text }]);
    setPending("");

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: SESSION_ID, message: text }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events split by blank line
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = raw.split("\n");
          let event = "message";
          const dataParts = [];
          for (const l of lines) {
            if (l.startsWith("event:")) event = l.slice(6).trim();
            else if (l.startsWith("data:")) dataParts.push(l.slice(5).replace(/^ /, ""));
          }
          const data = dataParts.join("\n");
          if (event === "done") continue;
          if (event === "error") {
            toast.error(data || "AI error");
            continue;
          }
          const chunk = data.replace(/\\n/g, "\n");
          full += chunk;
          setPending(full);
        }
      }

      setMessages((prev) => [...prev, { _mid: `a-${Date.now()}`, role: "assistant", content: full }]);
      setPending("");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[ai] chat failed:", e);
      toast.error("AI request failed");
      setPending("");
    } finally {
      setStreaming(false);
    }
  };

  const reset = async () => {
    try {
      await api.post(`/ai/reset/${SESSION_ID}`);
      setMessages([]);
      toast.success("Conversation cleared");
    } catch { toast.error("Could not clear"); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-0px)] md:h-screen">
      {/* header */}
      <div className="px-6 md:px-10 py-6 border-b border-[#E5E0D8] bg-[#F7F5F0]">
        <div className="max-w-5xl flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A] flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> GPT 5.6 Terra
            </div>
            <h1 className="heading text-2xl sm:text-3xl font-bold text-[#292723] mt-1.5">Ask your data</h1>
            <p className="text-sm text-[#5C5850] mt-1 max-w-xl">
              Ask anything about your NPs, facilities, patients, visits and CPT codes. The assistant answers only from your recorded data.
            </p>
          </div>
          <Button variant="outline" onClick={reset} className="border-[#E5E0D8]" data-testid="ai-reset-btn">
            <RefreshCcw className="h-4 w-4 mr-2" /> New chat
          </Button>
        </div>
      </div>

      {/* messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 md:px-10 py-6" data-testid="ai-messages">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.length === 0 && !pending && (
            <div className="bg-white border border-[#E5E0D8] rounded-md p-6">
              <div className="text-xs uppercase tracking-[0.06em] text-[#8A847A]">Try asking</div>
              <div className="mt-3 grid gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    disabled={streaming}
                    data-testid="ai-suggestion"
                    className="text-left px-4 py-2.5 rounded-md border border-[#E5E0D8] bg-[#F7F5F0] hover:bg-[#F0EBE1] transition-colors text-sm text-[#292723]"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) => (
            <MessageBubble key={m._mid || `${m.role}-${m.created_at}`} role={m.role} content={m.content} />
          ))}
          {pending && <MessageBubble role="assistant" content={pending} streaming />}
        </div>
      </div>

      {/* composer */}
      <div className="border-t border-[#E5E0D8] bg-white px-6 md:px-10 py-4">
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="max-w-3xl mx-auto flex gap-2"
          data-testid="ai-composer"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your NPs, facilities, patients, visits…"
            className="h-11 bg-white border-[#E5E0D8]"
            disabled={streaming}
            data-testid="ai-input"
          />
          <Button
            type="submit"
            disabled={streaming || !input.trim()}
            className="h-11 bg-[#4A6B5D] hover:bg-[#3E594D] text-white"
            data-testid="ai-send-btn"
          >
            <Send className="h-4 w-4 mr-2" /> {streaming ? "Thinking..." : "Send"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function MessageBubble({ role, content, streaming }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className={isUser ? "bg-[#4A6B5D] text-white" : "bg-[#F0EBE1] text-[#4A6B5D]"}>
          {isUser ? <UserIcon className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
        </AvatarFallback>
      </Avatar>
      <div
        className={`max-w-[85%] rounded-md px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
          isUser
            ? "bg-[#4A6B5D] text-white"
            : "bg-white border border-[#E5E0D8] text-[#292723]"
        }`}
      >
        {content}
        {streaming && <span className="inline-block w-2 h-4 align-middle ml-1 bg-[#4A6B5D] animate-pulse" />}
      </div>
    </div>
  );
}
